import React, { createElement, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { marketComponentsLoaded } from '@market/web-components';

const dashToPascalCase = (str) => str.toLowerCase().split("-").map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1)).join("");
const camelToDashCase = (str) => str.replace(/([A-Z])/g, (m) => `-${m[0].toLowerCase()}`);

const attachProps = (node, newProps, oldProps = {}) => {
  if (node instanceof Element) {
    const className = getClassName(node.classList, newProps, oldProps);
    if (className !== "") {
      node.className = className;
    }
    Object.keys(newProps).forEach((name) => {
      if (name === "children" || name === "style" || name === "ref" || name === "class" || name === "className" || name === "forwardedRef") {
        return;
      }
      if (name.indexOf("on") === 0 && name[2] === name[2].toUpperCase()) {
        const eventName = name.substring(2);
        const eventNameLc = eventName[0].toLowerCase() + eventName.substring(1);
        if (!isCoveredByReact(eventNameLc)) {
          syncEvent(node, eventNameLc, newProps[name]);
        }
      } else {
        node[name] = newProps[name];
        const propType = typeof newProps[name];
        if (propType === "boolean") {
          if (newProps[name] === true) {
            node.setAttribute(camelToDashCase(name), "");
          } else {
            node.removeAttribute(camelToDashCase(name));
          }
        } else if (propType === "string") {
          node.setAttribute(camelToDashCase(name), newProps[name]);
        }
      }
    });
  }
};
const getClassName = (classList, newProps, oldProps) => {
  const newClassProp = newProps.className || newProps.class;
  const oldClassProp = oldProps.className || oldProps.class;
  const currentClasses = arrayToMap(classList);
  const incomingPropClasses = arrayToMap(newClassProp ? newClassProp.split(" ") : []);
  const oldPropClasses = arrayToMap(oldClassProp ? oldClassProp.split(" ") : []);
  const finalClassNames = [];
  currentClasses.forEach((currentClass) => {
    if (incomingPropClasses.has(currentClass)) {
      finalClassNames.push(currentClass);
      incomingPropClasses.delete(currentClass);
    } else if (!oldPropClasses.has(currentClass)) {
      finalClassNames.push(currentClass);
    }
  });
  incomingPropClasses.forEach((s) => finalClassNames.push(s));
  return finalClassNames.join(" ");
};
const transformReactEventName = (eventNameSuffix) => {
  switch (eventNameSuffix) {
    case "doubleclick":
      return "dblclick";
  }
  return eventNameSuffix;
};
/**
 * Checks if an event is supported in the current execution environment.
 * @license Modernizr 3.0.0pre (Custom Build) | MIT
 */
const isCoveredByReact = (eventNameSuffix) => {
  if (typeof document === "undefined") {
    return true;
  } else {
    const eventName = "on" + transformReactEventName(eventNameSuffix);
    let isSupported = eventName in document;
    if (!isSupported) {
      const element = document.createElement("div");
      element.setAttribute(eventName, "return;");
      isSupported = typeof element[eventName] === "function";
    }
    return isSupported;
  }
};
const syncEvent = (node, eventName, newEventHandler) => {
  const eventStore = node.__events || (node.__events = {});
  const oldEventHandler = eventStore[eventName];
  if (oldEventHandler) {
    node.removeEventListener(eventName, oldEventHandler);
  }
  node.addEventListener(
    eventName,
    eventStore[eventName] = function handler(e) {
      if (newEventHandler) {
        newEventHandler.call(this, e);
      }
    }
  );
};
const arrayToMap = (arr) => {
  const map = /* @__PURE__ */ new Map();
  arr.forEach((s) => map.set(s, s));
  return map;
};

var __defProp$2 = Object.defineProperty;
var __defProps$2 = Object.defineProperties;
var __getOwnPropDescs$2 = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$2 = Object.getOwnPropertySymbols;
var __hasOwnProp$2 = Object.prototype.hasOwnProperty;
var __propIsEnum$2 = Object.prototype.propertyIsEnumerable;
var __defNormalProp$2 = (obj, key, value) => key in obj ? __defProp$2(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$2 = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$2.call(b, prop))
      __defNormalProp$2(a, prop, b[prop]);
  if (__getOwnPropSymbols$2)
    for (var prop of __getOwnPropSymbols$2(b)) {
      if (__propIsEnum$2.call(b, prop))
        __defNormalProp$2(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$2 = (a, b) => __defProps$2(a, __getOwnPropDescs$2(b));
const setRef = (ref, value) => {
  if (typeof ref === "function") {
    ref(value);
  } else if (ref != null) {
    ref.current = value;
  }
};
const mergeRefs = (...refs) => {
  return (value) => {
    refs.forEach((ref) => {
      setRef(ref, value);
    });
  };
};
const createForwardRef = (ReactComponent, displayName) => {
  const forwardRef = (props, ref) => {
    return /* @__PURE__ */ React.createElement(ReactComponent, __spreadProps$2(__spreadValues$2({}, props), { forwardedRef: ref }));
  };
  forwardRef.displayName = displayName;
  return React.forwardRef(forwardRef);
};

var __defProp$1 = Object.defineProperty;
var __defProps$1 = Object.defineProperties;
var __getOwnPropDescs$1 = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$1 = Object.getOwnPropertySymbols;
var __hasOwnProp$1 = Object.prototype.hasOwnProperty;
var __propIsEnum$1 = Object.prototype.propertyIsEnumerable;
var __defNormalProp$1 = (obj, key, value) => key in obj ? __defProp$1(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$1 = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$1.call(b, prop))
      __defNormalProp$1(a, prop, b[prop]);
  if (__getOwnPropSymbols$1)
    for (var prop of __getOwnPropSymbols$1(b)) {
      if (__propIsEnum$1.call(b, prop))
        __defNormalProp$1(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$1 = (a, b) => __defProps$1(a, __getOwnPropDescs$1(b));
var __objRest$1 = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp$1.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols$1)
    for (var prop of __getOwnPropSymbols$1(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum$1.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const createReactComponent = (tagName, ReactComponentContext, manipulatePropsFunction, defineCustomElement) => {
  if (defineCustomElement !== void 0) {
    defineCustomElement();
  }
  const displayName = dashToPascalCase(tagName);
  const ReactComponent = class extends React.Component {
    constructor(props) {
      super(props);
      this.setComponentElRef = (element) => {
        this.componentEl = element;
      };
    }
    componentDidMount() {
      this.componentDidUpdate(this.props);
    }
    componentDidUpdate(prevProps) {
      attachProps(this.componentEl, this.props, prevProps);
    }
    render() {
      const _a = this.props, { children, forwardedRef, style, className, ref } = _a, cProps = __objRest$1(_a, ["children", "forwardedRef", "style", "className", "ref"]);
      let propsToPass = Object.keys(cProps).reduce((acc, name) => {
        const value = cProps[name];
        if (name.indexOf("on") === 0 && name[2] === name[2].toUpperCase()) {
          const eventName = name.substring(2).toLowerCase();
          if (typeof document !== "undefined" && isCoveredByReact(eventName)) {
            acc[name] = value;
          }
        } else {
          const type = typeof value;
          if (type === "string" || type === "boolean" || type === "number") {
            acc[camelToDashCase(name)] = value;
          }
        }
        return acc;
      }, {});
      if (manipulatePropsFunction) {
        propsToPass = manipulatePropsFunction(this.props, propsToPass);
      }
      const newProps = __spreadProps$1(__spreadValues$1({}, propsToPass), {
        ref: mergeRefs(forwardedRef, this.setComponentElRef),
        style
      });
      return createElement(tagName, newProps, children);
    }
    static get displayName() {
      return displayName;
    }
  };
  if (ReactComponentContext) {
    ReactComponent.contextType = ReactComponentContext;
  }
  return createForwardRef(ReactComponent, displayName);
};

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const MARKET_TAGNAME_REGEX = /(market-)(.*)/;
const createReactOverlayComponent = (tagName, defineCustomElement) => {
  if (defineCustomElement !== void 0) {
    defineCustomElement();
  }
  const displayName = dashToPascalCase(tagName);
  const contextManagerTagName = tagName.replace(
    MARKET_TAGNAME_REGEX,
    "$1context-manager"
  );
  const contextTagName = tagName.replace(MARKET_TAGNAME_REGEX, "$1context");
  const MarketContext = createReactComponent(contextTagName);
  const ModalElement = createReactComponent(tagName);
  const createOrReturnContextManager = () => {
    const contextManager = document.querySelector(contextManagerTagName);
    if (!contextManager) {
      const contextManager2 = document.createElement(contextManagerTagName);
      document.body.appendChild(contextManager2);
      return contextManager2;
    }
    return contextManager;
  };
  const ReactComponent = (_a) => {
    var _b = _a, {
      children,
      forwardedRef,
      style,
      className,
      ref
    } = _b, cProps = __objRest(_b, [
      "children",
      "forwardedRef",
      "style",
      "className",
      "ref"
    ]);
    const containerRef = useRef(null);
    const contextManagerElement = createOrReturnContextManager();
    const propsToPass = Object.keys(cProps).reduce((acc, name) => {
      const value = cProps[name];
      if (name.indexOf("on") === 0 && name[2] === name[2].toUpperCase()) {
        acc[name] = value;
      } else {
        const type = typeof value;
        if (type === "string" || type === "boolean" || type === "number") {
          acc[camelToDashCase(name)] = value;
        }
      }
      return acc;
    }, {});
    const newProps = __spreadProps(__spreadValues({}, propsToPass), {
      ref: forwardedRef,
      style,
      className
    });
    useEffect(() => {
      const setupModal = async () => {
        var _a2;
        await marketComponentsLoaded(containerRef.current);
        await ((_a2 = containerRef.current) == null ? void 0 : _a2.open(
          containerRef.current.firstElementChild
        ));
        await contextManagerElement.activate();
      };
      setupModal().catch(() => null);
      return () => {
        if (contextManagerElement.childElementCount === 0) {
          contextManagerElement.deactivate();
        }
      };
    }, []);
    if (contextManagerElement === null) return null;
    return createPortal(
      /* @__PURE__ */ React.createElement(MarketContext, { ref: containerRef }, /* @__PURE__ */ React.createElement(ModalElement, __spreadValues({}, newProps), children)),
      contextManagerElement
    );
  };
  ReactComponent.displayName = displayName;
  return createForwardRef(ReactComponent, displayName);
};

const MarketAccessory = /* @__PURE__ */ createReactComponent("market-accessory");
const MarketAccordionItem = /* @__PURE__ */ createReactComponent("market-accordion-item");
const MarketActionCard = /* @__PURE__ */ createReactComponent("market-action-card");
const MarketActivityIndicator = /* @__PURE__ */ createReactComponent("market-activity-indicator");
const MarketActivityIndicatorBar = /* @__PURE__ */ createReactComponent("market-activity-indicator-bar");
const MarketBanner = /* @__PURE__ */ createReactComponent("market-banner");
const MarketBlade = /* @__PURE__ */ createReactOverlayComponent("market-blade");
const MarketButton = /* @__PURE__ */ createReactComponent("market-button");
const MarketButtonDropdown = /* @__PURE__ */ createReactComponent("market-button-dropdown");
const MarketButtonGroup = /* @__PURE__ */ createReactComponent("market-button-group");
const MarketCheckbox = /* @__PURE__ */ createReactComponent("market-checkbox");
const MarketChoiceButton = /* @__PURE__ */ createReactComponent("market-choice-button");
const MarketCodeDisplay = /* @__PURE__ */ createReactComponent("market-code-display");
const MarketCodeInput = /* @__PURE__ */ createReactComponent("market-code-input");
const MarketColorPicker = /* @__PURE__ */ createReactComponent("market-color-picker");
const MarketColorPickerGradient = /* @__PURE__ */ createReactComponent("market-color-picker-gradient");
const MarketColorPickerInput = /* @__PURE__ */ createReactComponent("market-color-picker-input");
const MarketColorSwatch = /* @__PURE__ */ createReactComponent("market-color-swatch");
const MarketColorSwatchList = /* @__PURE__ */ createReactComponent("market-color-swatch-list");
const MarketContentCard = /* @__PURE__ */ createReactComponent("market-content-card");
const MarketContext = /* @__PURE__ */ createReactComponent("market-context");
const MarketContextManager = /* @__PURE__ */ createReactComponent("market-context-manager");
const MarketDatePicker = /* @__PURE__ */ createReactComponent("market-date-picker");
const MarketDatePickerDate = /* @__PURE__ */ createReactComponent("market-date-picker-date");
const MarketDatePickerInputDate = /* @__PURE__ */ createReactComponent("market-date-picker-input-date");
const MarketDatePickerMenu = /* @__PURE__ */ createReactComponent("market-date-picker-menu");
const MarketDialog = /* @__PURE__ */ createReactOverlayComponent("market-dialog");
const MarketDivider = /* @__PURE__ */ createReactComponent("market-divider");
const MarketDragHandle = /* @__PURE__ */ createReactComponent("market-drag-handle");
const MarketDropdown = /* @__PURE__ */ createReactComponent("market-dropdown");
const MarketEmptyState = /* @__PURE__ */ createReactComponent("market-empty-state");
const MarketField = /* @__PURE__ */ createReactComponent("market-field");
const MarketFileUpload = /* @__PURE__ */ createReactComponent("market-file-upload");
const MarketFilter = /* @__PURE__ */ createReactComponent("market-filter");
const MarketFilterButton = /* @__PURE__ */ createReactComponent("market-filter-button");
const MarketFilterDropdownMenu = /* @__PURE__ */ createReactComponent("market-filter-dropdown-menu");
const MarketFilterGroup = /* @__PURE__ */ createReactComponent("market-filter-group");
const MarketFooter = /* @__PURE__ */ createReactComponent("market-footer");
const MarketHeader = /* @__PURE__ */ createReactComponent("market-header");
const MarketIcon = /* @__PURE__ */ createReactComponent("market-icon");
const MarketInlineSectionHeader = /* @__PURE__ */ createReactComponent("market-inline-section-header");
const MarketInlineStatus = /* @__PURE__ */ createReactComponent("market-inline-status");
const MarketInputPassword = /* @__PURE__ */ createReactComponent("market-input-password");
const MarketInputSearch = /* @__PURE__ */ createReactComponent("market-input-search");
const MarketInputText = /* @__PURE__ */ createReactComponent("market-input-text");
const MarketLink = /* @__PURE__ */ createReactComponent("market-link");
const MarketList = /* @__PURE__ */ createReactComponent("market-list");
const MarketModalFull = /* @__PURE__ */ createReactOverlayComponent("market-modal-full");
const MarketModalPartial = /* @__PURE__ */ createReactOverlayComponent("market-modal-partial");
const MarketPagination = /* @__PURE__ */ createReactComponent("market-pagination");
const MarketPaginationNav = /* @__PURE__ */ createReactComponent("market-pagination-nav");
const MarketPaginationPageSize = /* @__PURE__ */ createReactComponent("market-pagination-page-size");
const MarketPill = /* @__PURE__ */ createReactComponent("market-pill");
const MarketPopover = /* @__PURE__ */ createReactComponent("market-popover");
const MarketProgressTracker = /* @__PURE__ */ createReactComponent("market-progress-tracker");
const MarketProgressTrackerStep = /* @__PURE__ */ createReactComponent("market-progress-tracker-step");
const MarketQrcode = /* @__PURE__ */ createReactComponent("market-qrcode");
const MarketRadio = /* @__PURE__ */ createReactComponent("market-radio");
const MarketRow = /* @__PURE__ */ createReactComponent("market-row");
const MarketSegment = /* @__PURE__ */ createReactComponent("market-segment");
const MarketSegmentedControl = /* @__PURE__ */ createReactComponent("market-segmented-control");
const MarketSelect = /* @__PURE__ */ createReactComponent("market-select");
const MarketSheet = /* @__PURE__ */ createReactOverlayComponent("market-sheet");
const MarketStepper = /* @__PURE__ */ createReactComponent("market-stepper");
const MarketTab = /* @__PURE__ */ createReactComponent("market-tab");
const MarketTabList = /* @__PURE__ */ createReactComponent("market-tab-list");
const MarketTabPanel = /* @__PURE__ */ createReactComponent("market-tab-panel");
const MarketTable = /* @__PURE__ */ createReactComponent("market-table");
const MarketTableArea = /* @__PURE__ */ createReactComponent("market-table-area");
const MarketTableCell = /* @__PURE__ */ createReactComponent("market-table-cell");
const MarketTableColumn = /* @__PURE__ */ createReactComponent("market-table-column");
const MarketTableRow = /* @__PURE__ */ createReactComponent("market-table-row");
const MarketTableV2 = /* @__PURE__ */ createReactComponent("market-table-v2");
const MarketTableV2Cell = /* @__PURE__ */ createReactComponent("market-table-v2-cell");
const MarketTableV2Group = /* @__PURE__ */ createReactComponent("market-table-v2-group");
const MarketTableV2Row = /* @__PURE__ */ createReactComponent("market-table-v2-row");
const MarketTabs = /* @__PURE__ */ createReactComponent("market-tabs");
const MarketTag = /* @__PURE__ */ createReactComponent("market-tag");
const MarketTextarea = /* @__PURE__ */ createReactComponent("market-textarea");
const MarketTile = /* @__PURE__ */ createReactComponent("market-tile");
const MarketToast = /* @__PURE__ */ createReactComponent("market-toast");
const MarketToaster = /* @__PURE__ */ createReactComponent("market-toaster");
const MarketToggle = /* @__PURE__ */ createReactComponent("market-toggle");
const MarketTooltip = /* @__PURE__ */ createReactComponent("market-tooltip");

export { MarketAccessory, MarketAccordionItem, MarketActionCard, MarketActivityIndicator, MarketActivityIndicatorBar, MarketBanner, MarketBlade, MarketButton, MarketButtonDropdown, MarketButtonGroup, MarketCheckbox, MarketChoiceButton, MarketCodeDisplay, MarketCodeInput, MarketColorPicker, MarketColorPickerGradient, MarketColorPickerInput, MarketColorSwatch, MarketColorSwatchList, MarketContentCard, MarketContext, MarketContextManager, MarketDatePicker, MarketDatePickerDate, MarketDatePickerInputDate, MarketDatePickerMenu, MarketDialog, MarketDivider, MarketDragHandle, MarketDropdown, MarketEmptyState, MarketField, MarketFileUpload, MarketFilter, MarketFilterButton, MarketFilterDropdownMenu, MarketFilterGroup, MarketFooter, MarketHeader, MarketIcon, MarketInlineSectionHeader, MarketInlineStatus, MarketInputPassword, MarketInputSearch, MarketInputText, MarketLink, MarketList, MarketModalFull, MarketModalPartial, MarketPagination, MarketPaginationNav, MarketPaginationPageSize, MarketPill, MarketPopover, MarketProgressTracker, MarketProgressTrackerStep, MarketQrcode, MarketRadio, MarketRow, MarketSegment, MarketSegmentedControl, MarketSelect, MarketSheet, MarketStepper, MarketTab, MarketTabList, MarketTabPanel, MarketTable, MarketTableArea, MarketTableCell, MarketTableColumn, MarketTableRow, MarketTableV2, MarketTableV2Cell, MarketTableV2Group, MarketTableV2Row, MarketTabs, MarketTag, MarketTextarea, MarketTile, MarketToast, MarketToaster, MarketToggle, MarketTooltip, createReactComponent, createReactOverlayComponent };
